import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  DefaultTheme,
  HeaderInput,
  HeaderTemplate,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  Progress,
  SectionHeader,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { useGetAuditSettingsReportTypes } from '../../../../api-refactored/audit-settings/useGetAuditSettingsReportTypes';
import { usePostAuditReportListing } from '../../../../api-refactored/reports/usePostAuditReportListing';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../../utils/DateUtils';
import { configurationTypeData } from '../../logic/dropDownOption';

import { parseLocationSearch } from '../../../../utils/StringUtils';
import ConfirmReviewModal from './ConfirmReviewModal';

const useStyles = makeStyles()((theme) =>
({
  confirmButtonLabel: {
    height: 'none',
    fontSize: '18px',
    fontWeight: 800,
    lineHeight: '2rem',
    display: 'flex',
    color: DefaultTheme.color.link.linkDefault01,
    alignItems: 'flex-start',
    padding: theme.spacing(0),
    '&:hover': {
      color: theme.color.link.linkHover01,
    },
  },
}));

interface AuditReport {
  createdAt: string;
  fileKey: string;
  remarks: string;
  reviewDeadLine: string;
  reviewed: boolean;
  reviewedAt: string | null;
}

interface RequestInput {
  reportType?: string;
}

const useDataTableColumns = (state: TableState, onDownloadSuccess: () => void) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [confirmReviewModalOpen, setConfirmReviewModalOpen] = useState(true);

  const [selectedItem, setSelectedItem] = useState<AuditReport | undefined>(undefined);

  const openConfirmReviewModal = () => {
    setConfirmReviewModalOpen(true);
  };

  const closeConfirmReviewModal = () => {
    setConfirmReviewModalOpen(false);
    setSelectedItem(undefined);
  };

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'createdAt',
      label: t('admin->>AuditReport.DateOfReportGenerated'),
      options: {
        sort: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {stringToSemanticDisplay(state.data[dataIndex].createdAt)}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'ReviewDeadline',
      label: t('admin->>AuditReport.ReviewDeadline'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {stringToSemanticDisplay(state.data[dataIndex].reviewDeadLine)}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'DateTimeReviewed',
      label: t('admin->>AuditReport.DateTimeReviewed'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">
              {state.data[dataIndex].reviewedAt
                ? stringToLongDateAndShortTime(state.data[dataIndex].reviewedAt, true)
                : '-'}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'Remarks',
      label: t('admin->>AuditReport.Remarks'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].remarks || '-'}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'Actions',
      label: t('admin->>AuditReport.Actions'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {/* use state.data[dataIndex].fileKey for download link */}
            <RowDownloadButton
              fileKey={state.data[dataIndex].fileKey}
              onDownloadSuccess={onDownloadSuccess}
            />
          </>
        ),
      },
    },
    {
      name: 'Status',
      label: t('admin->>AuditReport.Status'),
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            {state.data[dataIndex].reviewed && (
              <Pill size="M" variant="positive" label="Reviewed" />
            )}
            {!state.data[dataIndex].reviewed && (
              <Button
                variant="text"
                className={classes.confirmButtonLabel}
                onClick={() => {
                  openConfirmReviewModal();
                  setSelectedItem(state.data[dataIndex]);
                }}
              >
                Confirm Review
              </Button>
            )}
          </Box>
        ),
      },
    },
  ];

  return {
    tableColumns,
    confirmReviewModalOpen,
    selectedItem,
    closeConfirmReviewModal,
  };
};

const findReportDescriptionByCode = (reportOptions: any, reportCode?: string) => {
  const foundOption = (reportOptions || []).find((item: any) => item.code === reportCode);
  return foundOption ? foundOption.description : '';
};

const auditReportRowsPerPageOptions = [10, 15, 20, 30, 50];

const auditReportTableDefaultState: TableState = {
  ...TABLE_INITIAL_STATE,
  size: 10,
  order: 'desc',
  sortColumn: 'createdAt',
};

const AuditReportListing = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const noResultsDisplay = t('admin->>AuditReport.NoReportMessage');

  const { snackbarHandler } = useSnackbarHandler();

  const onDownloadSuccess = () => {
    snackbarHandler?.setGlobalSnackbar({
      targetSeverity: 'success',
      targetData: t('admin->>AuditReport.DownloadSuccessMsg', {
        auditReportName: findReportDescriptionByCode(reportTypesData, requestInput.reportType),
      }),
    });
  };

  const { state, dispatch: tableDispatch, tOptions } = useDataTable(auditReportTableDefaultState);
  const {
    tableColumns,
    closeConfirmReviewModal,
    confirmReviewModalOpen,
    selectedItem,
  } = useDataTableColumns(state, onDownloadSuccess);

  const location = useLocation();

  useEffect(() => {
    const searchParams = parseLocationSearch(location.search);
    if (searchParams.report) setRequestInput({ reportType: searchParams.report || '' });
  }, [location]);

  const { data: reportTypesData, isLoading: isLoadingSetting } = useGetAuditSettingsReportTypes({
    onSuccess: () => null,
  });

  const [requestInput, setRequestInput] = useState<RequestInput>({});

  const { mutate: mutatePostAuditReportListing, isLoading } = usePostAuditReportListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
    pagination: { page: state.page, size: state.size, sort: state.sortColumn, order: state.order },
    reportType: requestInput.reportType || '',
  });

  useEffect(() => {
    if (requestInput.reportType) mutatePostAuditReportListing();
  }, [
    mutatePostAuditReportListing,
    state.page,
    state.size,
    state.sortColumn,
    state.order,
    requestInput.reportType,
  ]);

  if (isLoadingSetting)
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );

  return (
    <>
      <HeaderTemplate>
        <HeaderInput
          label="Report Name"
          input={
            <CustomSelect
              menuItems={configurationTypeData(reportTypesData || [])}
              placeholder={t('admin->>AuditReport.SelectReportName')}
              displayEmpty
              onChange={(e: any) => {
                setRequestInput({ reportType: e.target.value as string });
              }}
              value={requestInput.reportType}
              variant="filled"
              fullWidth
              isHeader
            />
          }
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <SectionHeader title={t('admin->>AuditReport.AuditReportSectionHeader')} />
        <Box>
          <MUIDataTable
            isLoading={isLoading}
            state={state}
            columns={tableColumns}
            dispatch={tableDispatch}
            data={state.data}
            emptyDataLabel={noResultsDisplay}
            options={{ ...tOptions, pagination: true, sort: true }}
            rowsPerPageOptions={auditReportRowsPerPageOptions}
          />
        </Box>
        <ConfirmReviewModal
          t={t}
          classes={classes}
          open={confirmReviewModalOpen}
          onCloseModal={closeConfirmReviewModal}
          selectedItem={selectedItem}
          onUpdateSuccess={() => {
            closeConfirmReviewModal();
            snackbarHandler?.setGlobalSnackbar({
              targetSeverity: 'success',
              targetData: t('admin->>AuditReport.SuccessMsg', {
                auditReportName: findReportDescriptionByCode(
                  reportTypesData,
                  requestInput.reportType
                ),
              }),
            });
            mutatePostAuditReportListing();
          }}
        />
      </ContentTemplate>
    </>
  );
};

interface RowDownloadButtonProps {
  fileKey: string;
  onDownloadSuccess: () => void;
}

const RowDownloadButton = (props: RowDownloadButtonProps) => {
  const [data, setData] = useState<string>('');
  const { fileKey, onDownloadSuccess } = props;

  const handleClicked = () => {
    setData(fileKey);
  };

  const handleDownloadError = () => {
    setData('');
  };

  return (
    <>
      <DownloadFromS3Action
        actionType="link"
        label="Download"
        requestRoute={{ data, url: '/api/s3/files' }}
        onClick={handleClicked}
        onError={handleDownloadError}
        onDownload={() => {
          onDownloadSuccess();
          setData('');
        }}
        isTableRowDownload
        disable
      />
    </>
  );
};

export default AuditReportListing;
